import React from 'react';
import { MarkdownRenderer } from '../common';
import { calculatePercentOriginal, formatPercentOriginal, isEdited } from '../../utils/textSimilarity';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const getMediaUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/uploads/')) {
    return `${API_URL}${url}`;
  }
  return url;
};

const AspectBadges = ({ aspects }) => {
  if (!aspects || aspects.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1 mt-1 mb-2">
      {aspects.map((aspect, idx) => (
        <span
          key={idx}
          className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700"
          title={aspect.aspectName}
        >
          {aspect.aspectName}
        </span>
      ))}
    </div>
  );
};

const BlockContent = ({ block, contributors }) => {
  // Find contributor info for player content
  const contributor = block.userId ? contributors[block.userId] : null;

  // GM text content types
  if (block.type === 'gm_prose' || block.type === 'gm_text') {
    return (
      <div className="pl-4 border-l-2 border-purple-300">
        <div className="mb-1">
          <span className="inline-block px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded">
            GM
          </span>
        </div>
        <MarkdownRenderer content={block.content} />
      </div>
    );
  }

  if (block.type === 'gm_transition') {
    return (
      <div className="pl-4 border-l-2 border-purple-200">
        <div className="mb-1">
          <span className="inline-block px-2 py-0.5 text-xs font-medium bg-purple-50 text-purple-600 rounded">
            GM
          </span>
        </div>
        <div className="text-gray-600 italic">
          <MarkdownRenderer content={block.content} />
        </div>
      </div>
    );
  }

  // GM media content types
  if (block.type === 'gm_image') {
    return (
      <div className="pl-4 border-l-2 border-purple-300">
        <div className="mb-1">
          <span className="inline-block px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded">
            GM
          </span>
        </div>
        {block.mediaUrl && (
          <figure className="my-2">
            <img
              src={getMediaUrl(block.mediaUrl)}
              alt="GM image"
              className="rounded-lg max-w-full h-auto"
            />
          </figure>
        )}
      </div>
    );
  }

  if (block.type === 'gm_audio') {
    return (
      <div className="pl-4 border-l-2 border-purple-300">
        <div className="mb-1">
          <span className="inline-block px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded">
            GM
          </span>
        </div>
        {block.mediaUrl && (
          <div className="my-2">
            <audio controls className="w-full">
              <source src={getMediaUrl(block.mediaUrl)} />
              Your browser does not support audio playback.
            </audio>
          </div>
        )}
      </div>
    );
  }

  // Player content
  if (block.type === 'player_content') {
    const displayName = contributor?.displayName || 'Unknown Player';
    const hasBeenEdited = block.originalContent && isEdited(block.originalContent, block.content);
    const percentOriginal = hasBeenEdited ? calculatePercentOriginal(block.originalContent, block.content) : null;

    const renderContent = () => {
      if (block.contentType === 'prose') {
        return (
          <>
            {hasBeenEdited && (
              <span className="inline-block text-xs px-1.5 py-0.5 mb-1 rounded bg-amber-100 text-amber-700">
                {formatPercentOriginal(percentOriginal) || 'edited by GM'}
              </span>
            )}
            <MarkdownRenderer content={block.content} />
          </>
        );
      }

      if (block.contentType === 'image') {
        const imageUrl = getMediaUrl(block.mediaUrl);
        if (imageUrl) {
          return (
            <figure className="my-2">
              <img
                src={imageUrl}
                alt={block.content || 'Player image'}
                className="rounded-lg max-w-full h-auto"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling?.classList.remove('hidden');
                }}
              />
              <div className="hidden text-sm text-gray-500 italic">
                [Image: {block.content || 'Could not load image'}]
              </div>
            </figure>
          );
        }
        // Fallback if no mediaUrl
        return (
          <div className="my-2 p-4 bg-gray-100 rounded text-sm text-gray-500 italic">
            [Image: {block.content || 'No image URL'}]
          </div>
        );
      }

      if (block.contentType === 'audio') {
        const audioUrl = getMediaUrl(block.mediaUrl);
        if (audioUrl) {
          return (
            <div className="my-2">
              <audio controls className="w-full">
                <source src={audioUrl} />
                Your browser does not support audio playback.
              </audio>
              {block.content && (
                <p className="text-sm text-gray-500 text-center mt-1">
                  {block.content}
                </p>
              )}
            </div>
          );
        }
        // Fallback if no mediaUrl
        return (
          <div className="my-2 p-4 bg-gray-100 rounded text-sm text-gray-500 italic">
            [Audio: {block.content || 'No audio URL'}]
          </div>
        );
      }

      // Unknown content type - show what we have
      return (
        <div className="text-gray-500 italic">
          {block.content || '[No content]'}
        </div>
      );
    };

    return (
      <div className="pl-4 border-l-2 border-blue-300">
        <div className="mb-1">
          <span className="inline-block px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded">
            {displayName}
          </span>
        </div>
        <AspectBadges aspects={block.referencedAspects} />
        {renderContent()}
      </div>
    );
  }

  return null;
};

export const PassageWithAttribution = ({ passage, participants }) => {
  if (!passage?.content || passage.content.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No passage content available.</p>
      </div>
    );
  }

  // Build a map of userId to participant info
  const contributors = {};
  if (participants) {
    participants.forEach(p => {
      contributors[p.id] = p;
    });
  }

  return (
    <div className="space-y-6">
      {passage.content.map((block, index) => (
        <div key={block.id || index}>
          <BlockContent block={block} contributors={contributors} />
        </div>
      ))}
    </div>
  );
};
